// Centralised SEO helpers — keeps title/description/OG/Twitter/canonical
// consistent across every route. Uses a single OG image fallback for
// generic pages; per-route loaders may override with content-specific
// images (e.g. car photos, blog cover art).
import { CONTACT } from "@/config/contact";

const SITE = CONTACT.websiteUrl.replace(/\/$/, "");
const SITE_NAME = "AutoXChange";
const DEFAULT_OG = `${SITE}/og-default.jpg`;
const DEFAULT_TWITTER = `${SITE}/og-twitter.jpg`;

export const SEO_DEFAULTS = {
  site: SITE,
  siteName: SITE_NAME,
  defaultOgImage: DEFAULT_OG,
  twitterHandle: undefined as string | undefined,
};

export interface SeoMetaInput {
  title: string;
  description: string;
  /** Path segment beginning with `/`. */
  path: string;
  image?: string;
  /** Alt text for og:image / twitter:image — improves a11y + SEO.
   *  When omitted, `buildSeoMeta` derives one from `title` + optional
   *  `imageAltContext` so we never ship a bare og:image with no alt. */
  imageAlt?: string;
  /** Structured hints used by `deriveImageAlt` when `imageAlt` is missing.
   *  Lets car/blog/city routes produce richer fallbacks than just the title. */
  imageAltContext?: ImageAltContext;
  ogType?: "website" | "article" | "product";
  /** When true, sets robots=noindex,nofollow. */
  noIndex?: boolean;
  /** When true, appends `noarchive` to the robots directive so search
   *  engines don't keep a cached snapshot of the page. Useful for pages
   *  whose content goes stale fast (e.g. sold/removed inventory) where
   *  an outdated cached "for sale" view would mislead buyers. Combines
   *  cleanly with `noIndex` (e.g. `noindex, nofollow, noarchive`). */
  noArchive?: boolean;
  /** Article-only fields. */
  publishedTime?: string;
  modifiedTime?: string;
  /** Optional local-SEO geo signals (lat/lon + region/placename). */
  geo?: {
    region?: string;
    placename?: string;
    latitude?: number;
    longitude?: number;
  };
}

/**
 * Structured fallback inputs for `deriveImageAlt`. Pass the richest fields
 * you have — the helper prefers domain-specific phrasing (car / blog / city)
 * over a plain title-based fallback when those fields are present.
 */
export type ImageAltContext =
  | {
      kind: "car";
      brand: string;
      model: string;
      variant?: string | null;
      year?: number | null;
    }
  | {
      kind: "blog";
      title: string;
    }
  | {
      kind: "city";
      cityName: string;
    }
  | {
      kind: "page";
      /** Optional override of the page title. Falls back to SeoMetaInput.title. */
      title?: string;
    };

/**
 * Derives accessible alt text for a page's primary image.
 *
 * Priority (first match wins):
 *  1. Explicit `imageAlt` passed by the caller (handled by buildSeoMeta).
 *  2. Domain-specific phrasing from `imageAltContext` (car/blog/city).
 *  3. The page title with a brand suffix.
 *
 * The output is always trimmed, single-spaced, and capped at 150 chars
 * (Meta/Twitter both truncate around 125-140 in practice; 150 is the
 * documented hard limit for og:image:alt and twitter:image:alt).
 */
export function deriveImageAlt(input: {
  title: string;
  context?: ImageAltContext;
}): string {
  const ctx = input.context;
  let raw: string;

  if (ctx?.kind === "car") {
    const variant = ctx.variant?.trim();
    const year = typeof ctx.year === "number" ? ` ${ctx.year}` : "";
    const carLabel = `${ctx.brand} ${ctx.model}${variant ? ` ${variant}` : ""}${year}`;
    raw = `${carLabel} — til salg hos ${SITE_NAME} i Børkop`;
  } else if (ctx?.kind === "blog") {
    raw = `Forsidebillede: ${ctx.title} — ${SITE_NAME} blog`;
  } else if (ctx?.kind === "city") {
    raw = `${SITE_NAME} — bilforhandler tæt på ${ctx.cityName}`;
  } else {
    const titleSrc = ctx?.kind === "page" && ctx.title ? ctx.title : input.title;
    // Normalise the title and skip the brand suffix entirely if the brand
    // name already appears anywhere in it — otherwise we'd produce
    // "Kontakt AutoXChange — AutoXChange" or "Om os | AutoXChange — AutoXChange".
    const cleanedTitle = titleSrc.replace(/\s+/g, " ").trim();
    const alreadyHasBrand = new RegExp(`\\b${SITE_NAME}\\b`, "i").test(cleanedTitle);
    if (!cleanedTitle) {
      raw = `${SITE_NAME} — bilforhandler i Trekantområdet`;
    } else if (alreadyHasBrand) {
      // Replace any "<sep> AutoXChange" trailing decoration with a consistent
      // em-dash separator so the output reads cleanly across pages.
      raw = cleanedTitle.replace(
        new RegExp(`\\s*[—\\-|]+\\s*${SITE_NAME}\\s*$`, "i"),
        ` — ${SITE_NAME}`,
      );
    } else {
      raw = `${cleanedTitle} — ${SITE_NAME}`;
    }
  }

  const normalised = raw.replace(/\s+/g, " ").trim();
  return normalised.length > 150 ? `${normalised.slice(0, 149).trimEnd()}…` : normalised;
}


type Meta =
  | { title: string }
  | { name: string; content: string }
  | { property: string; content: string };

type Link =
  | { rel: string; href: string }
  | { rel: "alternate"; href: string; hrefLang: string };

/**
 * Languages this site is published in, mapped to URL prefixes.
 *
 * AutoXChange currently serves a single Danish locale targeting Denmark, so
 * every alternate points back to the canonical URL. The `x-default` entry is
 * Google's recommended fallback for users whose locale doesn't match any of
 * the listed `hreflang` values — keeping it ensures international users still
 * land on the Danish page rather than being filtered out of results.
 *
 * If a future English (or other) locale ships, add an entry like
 * `{ hrefLang: "en", prefix: "/en" }` and the helper will emit
 * `<link rel="alternate" hreflang="en" href="https://autoxchange.dk/en/...">`
 * automatically for every route.
 */
const LOCALES: ReadonlyArray<{ hrefLang: string; prefix: string }> = [
  { hrefLang: "da-DK", prefix: "" },
  { hrefLang: "x-default", prefix: "" },
];

/** Builds a TanStack Start `head()` payload with full SEO meta. */
export function buildSeoMeta(input: SeoMetaInput): { meta: Meta[]; links: Link[] } {
  const url = `${SITE}${input.path.startsWith("/") ? input.path : `/${input.path}`}`;
  const usingDefaultOg = !input.image;
  const image = input.image || DEFAULT_OG;
  // For Twitter/X, use the higher-resolution 1600x900 variant when no
  // per-route image was provided. Per-route images (e.g. car photos) flow
  // through unchanged so social previews stay content-specific.
  const twitterImage = input.image || DEFAULT_TWITTER;
  const ogType = input.ogType ?? "website";

  const meta: Meta[] = [
    { title: input.title },
    { name: "description", content: input.description },
    { property: "og:title", content: input.title },
    { property: "og:description", content: input.description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: url },
    { property: "og:site_name", content: SITE_NAME },
    { property: "og:locale", content: "da_DK" },
    { property: "og:image", content: image },
    { property: "og:image:type", content: image.endsWith(".png") ? "image/png" : "image/jpeg" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: input.title },
    { name: "twitter:description", content: input.description },
    { name: "twitter:image", content: twitterImage },
  ];

  // Only emit width/height when we actually know them — i.e. our pre-rendered
  // 1200x630 default OG image. For per-route images (car photos, blog covers),
  // dimensions vary per asset; declaring 1200x630 falsely caused Facebook to
  // crop awkwardly. Letting the scraper read the real dimensions is correct.
  if (usingDefaultOg) {
    meta.push({ property: "og:image:width", content: "1200" });
    meta.push({ property: "og:image:height", content: "630" });
  }

  // Always emit og:image:alt + twitter:image:alt. If the route didn't pass
  // an explicit `imageAlt`, derive one from `title` + `imageAltContext` so
  // we never ship a bare og:image with empty alt text (a11y + SEO both lose
  // points). Strict callers can still pre-empt this by passing imageAlt.
  const resolvedAlt =
    input.imageAlt?.trim() ||
    deriveImageAlt({ title: input.title, context: input.imageAltContext });
  meta.push({ property: "og:image:alt", content: resolvedAlt });
  meta.push({ name: "twitter:image:alt", content: resolvedAlt });

  if (input.geo) {
    if (input.geo.region) meta.push({ name: "geo.region", content: input.geo.region });
    if (input.geo.placename) meta.push({ name: "geo.placename", content: input.geo.placename });
    if (typeof input.geo.latitude === "number" && typeof input.geo.longitude === "number") {
      meta.push({
        name: "geo.position",
        content: `${input.geo.latitude};${input.geo.longitude}`,
      });
      meta.push({
        name: "ICBM",
        content: `${input.geo.latitude}, ${input.geo.longitude}`,
      });
    }
  }

  // Robots directive — combine the index/follow signal with optional
  // `noarchive` so we can opt specific routes (sold cars, removed
  // inventory) out of Google's cached snapshot without touching the rest
  // of the indexing policy.
  const robotsParts: string[] = input.noIndex
    ? ["noindex", "nofollow"]
    : ["index", "follow", "max-image-preview:large"];
  if (input.noArchive) robotsParts.push("noarchive");
  meta.push({ name: "robots", content: robotsParts.join(", ") });

  if (ogType === "article") {
    if (input.publishedTime) {
      meta.push({ property: "article:published_time", content: input.publishedTime });
    }
    if (input.modifiedTime) {
      meta.push({ property: "article:modified_time", content: input.modifiedTime });
    }
  }

  const links: Link[] = [{ rel: "canonical", href: url }];

  // Hreflang alternates — explicit language/region targeting for Search.
  // Skip on noindexed pages (admin-adjacent, 404s) where alternates would
  // confuse Google about the canonical version. The path (incl. query/hash
  // stripping done upstream) is reused so localized URLs stay identical to
  // the canonical when only one locale exists.
  if (!input.noIndex) {
    const pathOnly = input.path.startsWith("/") ? input.path : `/${input.path}`;
    for (const locale of LOCALES) {
      links.push({
        rel: "alternate",
        hrefLang: locale.hrefLang,
        href: `${SITE}${locale.prefix}${pathOnly}`,
      });
    }
  }

  return { meta, links };
}

/** Truncates description to ~155 chars on a word boundary for safe meta length. */
export function clampDescription(text: string | null | undefined, max = 155): string {
  const clean = (text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const trimmed = clean.slice(0, max - 1);
  const lastSpace = trimmed.lastIndexOf(" ");
  return (lastSpace > max * 0.6 ? trimmed.slice(0, lastSpace) : trimmed) + "…";
}
